import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';
import Button from './Button';

interface ProductDetail {
  id: string;
  name: string;
  code: string;
  category: string;
  taxRate: number;
  description?: string;
  regulations?: string[];
  exemptions?: string[];
  calculationMethod?: string;
}

interface ProductDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: ProductDetail | null;
}

const ProductDetailModal = ({ isOpen, onClose, product }: ProductDetailModalProps) => {
  const [activeTab, setActiveTab] = useState<'details' | 'regulations' | 'calculation'>('details');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !product) return null;

  const tabs = [
    { id: 'details' as const, label: 'Detalhes', icon: 'FileText' },
    { id: 'regulations' as const, label: 'Regulamentações', icon: 'BookOpen' },
    { id: 'calculation' as const, label: 'Cálculo', icon: 'Calculator' }
  ];

  const modalContent = (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      
      <div
        className="relative bg-card rounded-lg shadow-modal w-full max-w-2xl max-h-[90vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 id="modal-title" className="text-xl font-semibold text-foreground">
              {product.name}
            </h2>
            <p className="text-sm text-muted-foreground font-mono mt-1">
              Código: {product.code}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-colors duration-150"
            aria-label="Fechar"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="border-b border-border">
          <nav className="flex px-6" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`panel-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 border-b-2 transition-all duration-150
                  ${activeTab === tab.id
                    ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                  }
                `}
              >
                <Icon name={tab.icon} size={16} color="currentColor" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'details' && (
            <div id="panel-details" role="tabpanel" className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Categoria</label>
                <p className="text-base text-foreground mt-1">{product.category}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground">Alíquota Tributária</label>
                <p className="text-2xl font-semibold text-primary mt-1 font-mono">
                  {product.taxRate}%
                </p>
              </div>
              
              {product.description && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Descrição</label>
                  <p className="text-base text-foreground mt-1 leading-relaxed">
                    {product.description}
                  </p>
                </div>
              )}

              {product.exemptions && product.exemptions.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Isenções Aplicáveis</label>
                  <ul className="mt-2 space-y-2">
                    {product.exemptions.map((exemption, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-foreground">{exemption}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {activeTab === 'regulations' && (
            <div id="panel-regulations" role="tabpanel" className="space-y-4">
              {product.regulations && product.regulations.length > 0 ? (
                <ul className="space-y-3">
                  {product.regulations.map((regulation, index) => (
                    <li key={index} className="p-4 bg-muted rounded-md">
                      <div className="flex items-start gap-3">
                        <Icon name="Scale" size={18} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-foreground leading-relaxed">{regulation}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-8">
                  <Icon name="FileX" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                  <p className="text-muted-foreground">Nenhuma regulamentação específica registrada</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'calculation' && (
            <div id="panel-calculation" role="tabpanel" className="space-y-4">
              {product.calculationMethod ? (
                <div className="space-y-4">
                  <div className="p-4 bg-muted rounded-md">
                    <label className="text-sm font-medium text-muted-foreground block mb-2">
                      Método de Cálculo
                    </label>
                    <p className="text-sm text-foreground leading-relaxed font-mono">
                      {product.calculationMethod}
                    </p>
                  </div>
                  
                  <div className="p-4 border-2 border-accent/20 bg-accent/5 rounded-md">
                    <div className="flex items-start gap-3">
                      <Icon name="Info" size={18} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-foreground leading-relaxed">
                        O cálculo é aplicado automaticamente durante o processamento da consulta tributária.
                        Valores são arredondados conforme legislação vigente.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon name="Calculator" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                  <p className="text-muted-foreground">Método de cálculo padrão aplicado</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
          <Button variant="default" iconName="Download" iconPosition="left">
            Exportar Detalhes
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ProductDetailModal;